import { useLocation } from 'react-router-dom';
import { useRef, useEffect, type ReactNode } from 'react';
import { PORTAL_BASE } from '../config/portalPaths';

/**
 * Lightweight CSS-based page transition wrapper.
 * Re-triggers the `page-transition-enter` animation on every route change
 * by cycling a key tied to location.pathname.
 *
 * Respects `no-page-transitions`, `no-animations`, and `reduced-animations`
 * body classes via CSS rules in index.css.
 */
export default function PageTransition({ children }: { children: ReactNode }) {
  const location = useLocation();
  const nodeRef = useRef<HTMLDivElement>(null);
  const isPortal = location.pathname === PORTAL_BASE || location.pathname.startsWith(`${PORTAL_BASE}/`);

  // Re-trigger animation on route change by removing & re-adding the class
  useEffect(() => {
    const el = nodeRef.current;
    if (!el) return;
    // Remove class to reset animation
    el.classList.remove('page-transition-enter');
    // Portal navigation keeps the shell stationary; only the outlet changes.
    if (isPortal) return;
    // Force reflow so browser recognises the class removal
    void el.offsetWidth;
    // Re-add to trigger animation
    el.classList.add('page-transition-enter');
  }, [location.pathname, isPortal]);

  return (
    <div ref={nodeRef} className={isPortal ? undefined : 'page-transition-enter'}>
      {children}
    </div>
  );
}
